import { lazy } from '@loadable/component';
import CommentTemplateList from '@modules/comment/views/CommentTemplateList';
import Dashboard from '@modules/dashboard/views/Dashboard';
import PageNotFound from '@modules/exception/PageNotFound';
import GradingLimitsList from '@modules/gradingLimits/views/GradingLimitsList';
import KYCReport from '@modules/investors/views/KYCReport';
import AdminLayout from '@modules/layout/views/AdminLayout';
import LogoLayout from '@modules/layout/views/LogoLayout';
import Suspense from '@modules/router/views/Suspense';
import SecondaryMarketTransactionsList from '@modules/secondaryMarket/views/SecondaryMarketTransactionsList';
import { unstable_HistoryRouter as HistoryRouter, Route, Routes } from 'react-router-dom';
import history from '../src/history';
import routeNames from '../src/routeNames';
import EInvoiceList from '@modules/eInvoice/views/EInvoiceList';

const Login = lazy(() => import('../../auth/views/Login'));
const Otp = lazy(() => import('../../auth/views/Otp'));
const ResetPassword = lazy(() => import('../../resetPassword/views/ResetPassword'));
const InvestorRecorder = lazy(() => import('../../investors/views/InvestorRecorder'));
const InvestorRecorderDetails = lazy(() => import('../../investors/views/InvestorRecorderDetails'));
const WithdrawalList = lazy(() => import('../../investors/views/WithdrawalList'));
const InvestorList = lazy(() => import('../../investors/views/InvestorList'));
const DepositList = lazy(() => import('../../investors/views/DepositList'));
const ManageUsersList = lazy(() => import('../../investors/views/ManageUsersList'));
const ServiceFeeRebateList = lazy(() => import('../../investors/views/ServiceFeeRebateList'));
const InvestorTransactionReportList = lazy(() => import('../../investors/views/InvestorTransactionReportList'));
const PortfolioType = lazy(() => import('../../portfolio/views/PortfolioType/PortfolioType'));
const PortfolioByInvoice = lazy(() => import('../../portfolio/views/ByInvoice'));
const ViewPortfolioByInvoice = lazy(() => import('../../portfolio/views/ViewPortfolioByInvoice'));
const PortfolioByTermLoan = lazy(() => import('../../portfolio/views/ByTermLoan'));
const AutoInvestByInvoice = lazy(() => import('../../autoInvest/views/ByInvoice'));
const AutoInvestByTermLoan = lazy(() => import('../../autoInvest/views/ByTermLoan'));
const AutoInvestExclusionList = lazy(() => import('../../autoInvest/views/ExclusionList'));
const AutoInvestSpecialAllocation = lazy(() => import('../../autoInvest/views/SpecialAllocation'));
const AutoInvestInternalFunding = lazy(() => import('../../autoInvest/views/InternalFunding'));
const OtherTransaction = lazy(() => import('../../otherTransaction/views/OtherTransaction'));
const ManageOtherIncome = lazy(() => import('../../otherTransaction/views/ManageOtherTransaction'));
const IntroducerList = lazy(() => import('../../introducer/views/IntroducerList'));
const IntroducerGroupList = lazy(() => import('../../introducer/views/IntroducerGroupList'));
const ReferralCodeList = lazy(() => import('../../introducer/views/ReferralCodeList'));
const IntroducerReferralCode = lazy(() => import('../../introducer/views/introducerReferralCode'));
const UpcomingStatement = lazy(() => import('../../statement/views/UpcomingStatement'));
const ReleasedStatement = lazy(() => import('../../statement/views/ReleasedStatement'));
const GenerateStatement = lazy(() => import('../../statement/views/GenerateStatement'));
const AddDepositWithdrawal = lazy(() => import('../../investors/views/AddDepositWithdrawal'));

const Router = () => (
  <HistoryRouter history={history}>
    <Routes>
      <Route element={<Suspense />}>
        <Route element={<LogoLayout />}>
          <Route path={routeNames.home} element={<Login />} />
          <Route path={routeNames.otp} element={<Otp />} />
          <Route path={routeNames.resetPassword} element={<ResetPassword />}>
            <Route path=":token" element={<ResetPassword />} />
          </Route>
        </Route>
        <Route element={<AdminLayout />}>
          <Route element={<Suspense />}>
            <Route path={routeNames.dashboard} element={<Dashboard />} />
            <Route path={routeNames.investorList} element={<InvestorList />} />
            <Route path={`${routeNames.kycReport}/:userId`} element={<KYCReport />} />
            <Route path={routeNames.deposit} element={<DepositList />} />
            <Route path={`${routeNames.investorRecorderDetails}/:id`} element={<InvestorRecorderDetails />} />
            <Route path={routeNames.investorRecorder} element={<InvestorRecorder />} />
            <Route path={routeNames.manageUsersList} element={<ManageUsersList />} />
            <Route path={routeNames.serviceFeeRebateList} element={<ServiceFeeRebateList />} />
            <Route path={routeNames.investorTransactionReport} element={<InvestorTransactionReportList />} />
            <Route path={routeNames.withdrawal} element={<WithdrawalList />} />
            <Route path={routeNames.portfolioType} element={<PortfolioType />} />
            <Route path={routeNames.portfolioByInvoice} element={<PortfolioByInvoice />} />
            <Route path={`${routeNames.portfolioByInvoiceView}/:id`} element={<ViewPortfolioByInvoice />} />
            <Route path={routeNames.portfolioByTermLoan} element={<PortfolioByTermLoan />} />
            <Route path={routeNames.autoInvestByInvoice} element={<AutoInvestByInvoice />} />
            <Route path={routeNames.autoInvestByTermLoan} element={<AutoInvestByTermLoan />} />
            <Route path={routeNames.autoInvestExclusionList} element={<AutoInvestExclusionList />} />
            <Route path={routeNames.autoInvestSpecialAllocation} element={<AutoInvestSpecialAllocation />} />
            <Route path={routeNames.autoInvestInternalFunding} element={<AutoInvestInternalFunding />} />
            <Route path={routeNames.otherTransactionList} element={<OtherTransaction />} />
            <Route path={routeNames.otherTransactionManage} element={<ManageOtherIncome />} />
            <Route path={routeNames.introducerList} element={<IntroducerList />} />
            <Route path={routeNames.introducerGroupList} element={<IntroducerGroupList />} />
            <Route path={routeNames.referralCodeList} element={<ReferralCodeList />} />
            <Route path={`${routeNames.introducerReferralCode}/:companyIntroducerId`} element={<IntroducerReferralCode />} />
            <Route path={routeNames.upcomingStatement} element={<UpcomingStatement />} />
            <Route path={routeNames.releasedStatement} element={<ReleasedStatement />} />
            <Route path={routeNames.generateStatement} element={<GenerateStatement />} />
            <Route path={routeNames.addDeposit} element={<AddDepositWithdrawal />} />
            <Route path={routeNames.addWithdrawal} element={<AddDepositWithdrawal />} />
            <Route path={routeNames.commentTemplateList} element={<CommentTemplateList />} />
            <Route path={routeNames.gradingLimitsList} element={<GradingLimitsList />} />
            <Route path={routeNames.secondaryMarketTransactionsList} element={<SecondaryMarketTransactionsList />} />
            <Route path={routeNames.eInvoice} element={<EInvoiceList />} />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  </HistoryRouter>
);

export default Router;
